
import {
  ArrowDownTrayIcon,
  ArrowLongLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LockOpenIcon,
  PencilIcon,
} from "@heroicons/vue/24/solid";
import { format, parseISO, getYear, getMonth, parse } from "date-fns";
import deepEqual from "deep-equal";
import { v4 as uuidv4 } from "uuid";
import { defineComponent } from "vue";
import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { segmentAnalytics } from "@/assets/scripts";
import InfoTooltip from "@/components/UI/InfoTooltip.vue";
import LoadingOverlay from "@/components/UI/LoadingOverlay.vue";
import LoadingSection from "@/components/UI/LoadingSection.vue";
import LoadingSpinner from "@/components/UI/LoadingSpinner.vue";
import MainButton from "@/components/UI/MainButton.vue";
import CustomGallery from "@/components/report/CustomGallery.vue";
import Header from "@/components/report/Header.vue";
import BaseSection from "@/components/report/partials/BaseSection.vue";
import GroupedProcesses from "@/components/report/partials/GroupedProcesses.vue";
import NotesSection from "@/components/report/partials/NotesSection.vue";
import { useConfirmationModal, useSaveBeforeLeaveConfirmationModal } from "@/composables/toast";
import DailyReportRepository from "@/repositories/DailyReportRepository";
import logger from "@/services/logger";
import {
  ReportPagination,
  ReportMeta,
  ReportProjectMeta,
  ReportData,
  ReportCustomFields,
  ReportProcessEntry,
  ReportSettings,
  ReportSection,
  ReportImageEntry,
  ReportImageEntrySelected,
  ReportComboValues,
  ReportListBasedEntry,
  ReportListBasedSection,
  ReportSectionsConfig,
} from "@/types/DailyReport";
import NavigationComponent from "@/views/partials/NavigationComponent";
import { sectionsConfig } from "@/views/report/daily_report/config";

export default defineComponent({
  name: "Single Report",
  components: {
    NavigationComponent,
    ArrowLongLeftIcon,
    ArrowDownTrayIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckIcon,
    PencilIcon,
    LockOpenIcon,
    CheckCircleIcon,
    MainButton,
    BaseSection,
    NotesSection,
    Header,
    LoadingOverlay,
    CustomGallery,
    GroupedProcesses,
    LoadingSection,
    InfoTooltip,
    LoadingSpinner,
  },
  data() {
    return {
      sectionsConfig: sectionsConfig as ReportSectionsConfig,
      editMode: false,
      loading: true,
      isSubmitting: false,
      showStayOnPageModal: false,
      message: {
        title: "",
        content: "",
      },
      to: "",
      leavePageAction: "",
      cancel: "",
      logoUrl: "" as string,
      reportPagination: null as ReportPagination | null,
      reportMeta: null as ReportMeta | null,
      projectMeta: null as ReportProjectMeta | null,
      reportData: null as ReportData | null,
      reportDataInitial: null as ReportData | null,
      reportCustomFields: null as ReportCustomFields | null,
      reportSettings: null as ReportSettings | null,
      oaiProcesses: [] as ReportProcessEntry[],
      loadedReport: false,
      highlightedSection: "" as ReportSection | "header" | "gallery" | "",
      validationError: false,
      isLoadingReport: false,
      exportLoading: false,
    };
  },
  mounted() {
    segmentAnalytics("daily_report_overview_single");
    this.getSingleReport();
  },

  beforeRouteLeave(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) {
    if (!this.editMode) {
      next();
    } else {
      this.showSaveBeforeLeaveConfirmationModal().then((confirmed) => {
        if (confirmed) {
          next();
        }
      });
    }
  },
  computed: {
    isApproved() {
      return this.reportMeta?.status === "APPROVED";
    },
    currentYear() {
      return getYear(parse(this.$route.params.date as string, "yyyy-MM-dd", new Date()));
    },
    currentMonth() {
      return getMonth(parse(this.$route.params.date as string, "yyyy-MM-dd", new Date())) + 1;
    },
    canSaveReport() {
      return (
        this.reportMeta?.status === "NO_EDIT" ||
        (this.reportData && !deepEqual(this.reportData, this.reportDataInitial))
      );
    },
    formattedDate() {
      return format(new Date(this.$route.params.date as string), "dd.MM.yyyy");
    },
  },
  watch: {
    highlightedSection() {
      this.scrollToSection();
    },
    editMode(value) {
      if (value) {
        this.scrollToSection();
      }
    },
  },
  methods: {
    getSingleReport() {
      this.isLoadingReport = true;

      const { customer_name, site_id, date } = this.$route.params;

      return DailyReportRepository.getDailyReport(
        customer_name as string,
        site_id as string,
        date as string,
      )
        .then((response) => {
          this.reportPagination = response.pagination;
          this.reportMeta = response.report_meta;
          this.projectMeta = response.project_meta;
          this.reportData = response.data;
          this.reportDataInitial = structuredClone(response.data);
          this.reportSettings = response.settings;
          this.reportCustomFields = response.custom_fields;
          this.oaiProcesses = response.oai_processes;
          this.logoUrl = response.logo_url;
          this.loading = false;

          this.sortSections();
        })
        .catch((error) => {
          logger.error(error);
        })
        .finally(() => {
          this.isLoadingReport = false;
        });
    },
    edit() {
      this.validationError = false;
      if (this.isApproved) {
        this.updateReportStatus("CUSTOMER_EDIT");
      }
      this.editMode = true;
    },
    cancelEdit() {
      this.editMode = false;
      this.highlightedSection = "";
      this.reportData = structuredClone(this.reportDataInitial);
      this.validationError = false;
    },
    approve() {
      if (this.editMode) {
        this.save();
      }
      if (!this.validationError && this.reportData) {
        this.showToast("success", this.$t("report.report_approved"));
        this.updateReportStatus("APPROVED");
      }
    },
    formatTimeToIsoTimestamp(date: string, time: string) {
      return `${date}T${time}:00.000+00:00`;
    },
    isValidData() {
      if (!this.reportData) {
        return false;
      }

      let isValid = true;

      const validateCombobox = (fieldType: string, value: ReportComboValues): boolean => {
        if (fieldType === "combobox") {
          return !!value.value;
        }
        return true;
      };

      const validateNumber = (fieldType: string, value: unknown): boolean => {
        if (fieldType === "number" && typeof value === "number") {
          return value >= 0;
        }
        return true;
      };

      Object.keys(this.reportData).forEach((key) => {
        if (!this.reportData) {
          return;
        }
        const sectionData = this.reportData[key as keyof ReportData];

        if (Array.isArray(sectionData)) {
          sectionData.forEach((item) => {
            for (const [itemKey, value] of Object.entries(item)) {
              if (key in this.sectionsConfig) {
                const section = this.sectionsConfig[key as keyof ReportSectionsConfig] as Record<
                  string,
                  unknown
                >;
                if (itemKey in section) {
                  const field = section[itemKey] as Record<string, unknown>;
                  const field_type = field["field_type"] as string;

                  if (!validateCombobox(field_type, value) || !validateNumber(field_type, value)) {
                    isValid = false;
                  }
                }
              }
            }
          });
        }
      });

      return isValid;
    },
    getSiteDurationChange(
      siteDuration: { start_time: string | null; end_time: string | null },
      date: string,
    ) {
      return {
        key: "site_duration",
        data: {
          start_time: siteDuration.start_time
            ? this.formatTimeToIsoTimestamp(date as string, siteDuration.start_time as string)
            : null,
          end_time: siteDuration.start_time
            ? this.formatTimeToIsoTimestamp(date as string, siteDuration.end_time as string)
            : null,
        },
      };
    },
    getProcessesChange(processes: ReportProcessEntry[], date: string) {
      return {
        key: "processes",
        data: processes.map((process) => ({
          process_type: process.process_type,
          location: process.location,
          note: process.note,
          start_time: process.start_time
            ? this.formatTimeToIsoTimestamp(date as string, process.start_time as string)
            : null,
          end_time: process.end_time
            ? this.formatTimeToIsoTimestamp(date as string, process.end_time as string)
            : null,
        })),
      };
    },
    getImagesChange(images: ReportImageEntry[]) {
      return {
        key: "images",
        data: images.map(({ id, source, camera_id, name, timestamp, width, height }) => ({
          id,
          source,
          camera_id,
          name,
          timestamp,
          width,
          height,
        })),
      };
    },

    async save() {
      this.isSubmitting = true;

      if (!this.isValidData()) {
        this.validationError = true;
        this.isSubmitting = false;
        this.showToast("error", this.$t("err.empty_values"));
        return;
      }

      const { customer_name, site_id, date } = this.$route.params;
      const changes = [] as Record<string, unknown>[];

      if (!this.reportData) {
        return;
      }

      Object.keys(this.reportData).forEach((key) => {
        if (
          this.reportData &&
          this.reportDataInitial &&
          (!deepEqual(
            this.reportData[key as ReportSection],
            this.reportDataInitial[key as ReportSection],
          ) ||
            (this.reportMeta?.status === "NO_EDIT" &&
              (key === "processes" ||
                (key === "site_duration" &&
                  this.reportData.site_duration.start_time &&
                  this.reportData.site_duration.end_time))))
        ) {
          if (key === "site_duration") {
            changes.push(this.getSiteDurationChange(this.reportData.site_duration, date as string));
          } else if (key === "processes") {
            changes.push(this.getProcessesChange(this.reportData.processes, date as string));
          } else if (key === "images" && this.reportData.images) {
            changes.push(this.getImagesChange(this.reportData.images));
          } else {
            changes.push({
              key: key,
              data: this.reportData[key as ReportSection],
            });
          }
        }
      });

      if (changes.length > 0) {
        await DailyReportRepository.updateDailyReport(
          customer_name as string,
          site_id as string,
          date as string,
          { changes },
        ).then(async () => {
          await this.getSingleReport();
        });
      }
      this.validationError = false;
      this.editMode = false;
      this.highlightedSection = "";
      this.isSubmitting = false;
    },
    handleAddCustomField(
      customFieldAdded: ReportComboValues,
      sectionKey: keyof ReportCustomFields,
      fieldKey: keyof ReportCustomFields[typeof sectionKey],
    ) {
      if (this.reportCustomFields) {
        const section = this.reportCustomFields[sectionKey];
        if (section && section[fieldKey]) {
          (section[fieldKey] as ReportComboValues[]).push(customFieldAdded);
        }
      }
    },
    handleDeleteCustomField(
      customFieldToDelete: ReportComboValues,
      sectionKey: keyof ReportCustomFields,
      fieldKey: keyof ReportCustomFields[typeof sectionKey],
    ) {
      if (this.reportCustomFields && this.reportData) {
        const customFieldList = this.reportCustomFields[sectionKey][
          fieldKey
        ] as ReportComboValues[];

        for (let i = 0; i < customFieldList.length; i++) {
          if (deepEqual(customFieldToDelete, customFieldList[i])) {
            customFieldList.splice(i, 1);
            for (let i = 0; i < this.reportData[sectionKey].length; i++) {
              const valueToClear = this.reportData[sectionKey][i][fieldKey] as ReportComboValues;
              if (deepEqual(valueToClear, customFieldToDelete)) {
                valueToClear.mode = "custom";
                valueToClear.value = "";
              }
            }
            break;
          }
        }
      }
    },
    updateReportStatus(status: string) {
      const payload = { status };
      const { customer_name, site_id, date } = this.$route.params;
      return DailyReportRepository.updateDailyReportStatus(
        customer_name as string,
        site_id as string,
        date as string,
        payload,
      )
        .then((response) => {
          this.reportMeta = response;
        })
        .catch((error) => {
          logger.error(error);
        });
    },
    updateSiteDuration(event: { target: HTMLInputElement }) {
      if (this.reportData && event.target.name in this.reportData.site_duration) {
        this.reportData.site_duration[event.target.name as "start_time" | "end_time"] =
          event.target.value !== "" ? event.target.value : null;
      }
    },
    updateGalleryImages(data: {
      galleryMap: Record<string, ReportImageEntrySelected[]>;
      customImages: ReportImageEntry[];
    }) {
      for (const key in data.galleryMap) {
        data.galleryMap[key].forEach((img) => {
          if (
            this.reportData &&
            img.selected &&
            !this.reportData?.images.some(
              (item) => item.camera_id === img.camera_id && item.timestamp === img.timestamp,
            )
          ) {
            this.reportData.images = [
              ...this.reportData.images,
              {
                url: img.url,
                id: uuidv4(),
                source: "gallery",
                camera_id: img.camera_id,
                name: img.name,
                timestamp: img.timestamp,
                width: 500,
                height: 500,
              },
            ];
          } else if (
            !img.selected &&
            this.reportData?.images.some(
              (item) => item.camera_id === img.camera_id && item.timestamp === img.timestamp,
            )
          ) {
            this.reportData.images = this.reportData?.images.filter(
              (item) => !(item.camera_id === img.camera_id && item.timestamp === img.timestamp),
            );
          }
        });
      }
      if (this.reportData) {
        this.reportData.images = [...this.reportData.images, ...data.customImages];
      }
    },
    removeGalleryImage(idToRemove: string) {
      if (this.reportData) {
        this.reportData.images = this.reportData.images.filter((item) => item.id !== idToRemove);
      }
    },

    addSectionRowEntry<T extends ReportListBasedSection>(
      sectionEntry: Extract<ReportListBasedEntry, { section: T }>,
      section: T,
    ) {
      if (this.reportData) {
        this.reportData[section].push(sectionEntry);
      }
    },
    removeSectionRowEntry<T extends ReportListBasedSection>(
      sectionEntry: Extract<ReportListBasedEntry, { section: T }>,
      section: T,
    ) {
      if (this.reportData) {
        const index = this.reportData[section].findIndex((e) => e === sectionEntry);

        if (index > -1) {
          this.reportData[section].splice(index, 1);
        }
      }
    },
    updateProcessFieldValue(event: { target: HTMLInputElement }, entryId: string) {
      if (this.reportData) {
        const processEntry = this.reportData.processes.find((item) => item.id === entryId);
        const key = event.target.name as "start_time" | "end_time" | "note";
        if (processEntry) {
          processEntry[key] = event.target.value;
        }
      }
    },
    updateProcessComboBox(
      field: "process_type" | "location",
      value: ReportComboValues,
      entryId: string,
    ) {
      if (this.reportData) {
        const processEntry = this.reportData.processes.find((item) => item.id === entryId);
        if (processEntry) {
          processEntry[field] = value;
        }
      }
    },
    updateSectionFieldValue(
      event: { target: HTMLInputElement },
      section: ReportListBasedSection,
      rowIdx: number,
    ) {
      if (this.reportData) {
        const entry = this.reportData[section][rowIdx] as Record<string, unknown>;
        const key = event.target.name;
        if (entry) {
          entry[key] =
            event.target.type === "number" ? Number(event.target.value) : event.target.value;
        }
      }
    },
    updateSectionComboBox(
      section: keyof ReportCustomFields,
      field: keyof ReportCustomFields[typeof section],
      value: ReportComboValues,
      rowIdx: number,
    ) {
      if (this.reportData?.[section]?.[rowIdx]?.[field]) {
        const dataToUpdate = this.reportData[section][rowIdx][field] as ReportComboValues;
        dataToUpdate.value = value.value;
        dataToUpdate.mode = value.mode;
      }
    },
    updateSectionData<T extends keyof ReportData>(sectionData: ReportData[T], key: T) {
      if (this.reportData) {
        this.reportData[key] = sectionData;
      }
    },
    scrollToSection() {
      this.$nextTick(() => {
        const element = document.getElementById(this.highlightedSection);
        if (element) {
          const isLastElement = this.checkElements(element, 1);
          const secondLast = this.checkElements(element, 2);
          const blockAttribute = isLastElement || secondLast ? "end" : "center";
          element.scrollIntoView({ behavior: "smooth", block: blockAttribute, inline: "nearest" });
        }
      });
    },
    checkElements(element: unknown, position: number) {
      const sectionContainers = document.querySelectorAll("#sections-container > div");
      const lastElement =
        sectionContainers.length >= 2
          ? sectionContainers[sectionContainers.length - position]
          : null;

      return element === lastElement;
    },
    formatLastUpdated(timeDate: string) {
      return format(parseISO(timeDate), "dd.MM.yyyy HH:mm:ss");
    },

    paginate(value: string) {
      if (!this.editMode) {
        this.$router.push({ name: "SingleDailyReport", params: { date: value } });
      } else {
        this.showSaveBeforeLeaveConfirmationModal().then((confirmed) => {
          if (confirmed) {
            this.$router.push({ name: "SingleDailyReport", params: { date: value } });
          }
        });
      }
    },

    exportPdf() {
      if (!this.editMode) {
        segmentAnalytics("daily_report_single_pdf");
        this.exportLoading = true;
        const lang = localStorage.getItem("lang") || "de";

        const { customer_name, site_id, date } = this.$route.params;

        return DailyReportRepository.exportDailyReportSingle(
          customer_name as string,
          site_id as string,
          date as string,
          lang,
          "2",
        )
          .then((response) => {
            const a = document.createElement("a"); //Create <a>
            a.href = "data:application/pdf;base64," + response.report;
            ``;
            a.download = `${this.projectMeta?.name}-${date}.pdf`; //File name Here
            a.click(); //Downloaded file
          })
          .catch((error) => {
            logger.error(error);
          })
          .finally(() => {
            this.exportLoading = false;
          });
      } else {
        this.showStayOnPageModal = true;
        this.message.content = this.$t("err.save_before_export");
        this.message = {
          title: this.$t("err.save_before_leave_title"),
          content: this.$t("err.save_before_export"),
        };
        this.leavePageAction = this.$t("buttons.export");
        this.cancel = this.$t("buttons.stay_page");
      }
    },
    sortSections() {
      const container = document.getElementById("sections-container");
      if (!container) {
        return;
      }
      const divs: HTMLDivElement[] = Array.from(container.children) as HTMLDivElement[];

      divs.forEach((div: HTMLDivElement) => {
        const id = div.getAttribute("id");
        if (id && this.reportSettings?.sections[id]?.order !== undefined) {
          const orderValue = String(this.reportSettings.sections[id].order);
          div.setAttribute("data-order", orderValue);
        }
      });

      divs.sort((a, b) => {
        const orderA = Number(a.getAttribute("data-order"));
        const orderB = Number(b.getAttribute("data-order"));
        return orderA - orderB;
      });
      divs.forEach((div) => container.appendChild(div));
    },
  },
  setup() {
    const showSaveBeforeLeaveConfirmationModal = useSaveBeforeLeaveConfirmationModal();
    const showConfirmationModal = useConfirmationModal();

    return {
      showSaveBeforeLeaveConfirmationModal,
      showConfirmationModal,
    };
  },
});
